import { ChangeDetectionStrategy, Component, TemplateRef, computed, input, viewChild } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { DataTableComponent, TableColumn, TableConfig } from '@components/data-table/data-table.component';
import { ServerTranslatePipe } from '@app/utils/pipes/server-translate-pipe';

const statusColumns = [
  { field: 'Completed', header: 'Completed' },
  { field: 'Pending', header: 'Pending' },
  { field: 'Running', header: 'Running' },
  { field: 'Failed', header: 'Failed' },
  { field: 'Unscheduled', header: 'FIELD_SEGMENTATION_UNSCHEDULED' },
] as const;

type Status = typeof statusColumns[number]['field'];

interface RegionLabel {
  id: string;
  names: unknown;
}

interface RegionCount {
  id: string;
  count: number;
  region?: RegionLabel;
}

interface StatusCell {
  count: number;
  regions: RegionCount[];
}

interface ReportRow {
  month: string;
  total: number;
  statuses: Record<Status, StatusCell>;
}

interface RegionReport extends RegionLabel {
  rows: ReportRow[];
}

interface ReportView {
  overall: ReportRow[];
  regions: RegionReport[];
}

@Component({
  selector: 'app-field-segmentation-monthly-report',
  standalone: true,
  imports: [DataTableComponent, ServerTranslatePipe, TranslatePipe],
  templateUrl: './field-segmentation-monthly-report.html',
  styleUrl: './field-segmentation-monthly-report.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldSegmentationMonthlyReportComponent {
  readonly output = input.required<string>();
  protected readonly report = computed(() => readReport(this.output()));
  protected readonly tableConfig: TableConfig = { paginator: false, keepOrder: true };
  private readonly monthCell = viewChild<TemplateRef<unknown>>('monthCell');
  private readonly statusCell = viewChild<TemplateRef<unknown>>('statusCell');
  protected readonly columns = computed<TableColumn[]>(() => [
    { field: 'month', header: 'Month', type: 'custom', customTemplate: this.monthCell() },
    { field: 'total', header: 'Total', type: 'number' },
    ...statusColumns.map(column => ({
      ...column,
      type: 'custom' as const,
      customTemplate: this.statusCell(),
    })),
  ]);
}

function readReport(output: string): ReportView | null {
  let root: Record<string, unknown>;
  try {
    root = record(JSON.parse(output));
  } catch {
    return null;
  }

  if (!Array.isArray(root['Months']) || !isCountRow(root['Total'])) {
    return null;
  }

  const regions = entries(root['Regions']).flatMap(value => {
    const region = record(value);
    const id = region['RegionId'];
    if (typeof id !== 'string' || !isCountRow(region['Total'])) {
      return [];
    }
    const names = record(region['RegionNames']);
    return [{
      id,
      names: typeof names['ValuesJson'] === 'string'
        ? { valuesJson: names['ValuesJson'] }
        : region['RegionNames'],
      rows: [...entries(region['Months']), region['Total']].filter(isCountRow).map(row => readRow(row)),
    }];
  });
  const labels = new Map(regions.map(region => [region.id, region]));
  const overall = root['Months'].filter(isCountRow).map(row => readRow(row, labels));
  return { overall: [...overall, readRow(root['Total'], labels)], regions };
}

function readRow(
  row: Record<string, unknown> & { Month: string; Total: number } & Record<Status, number>,
  labels?: ReadonlyMap<string, RegionLabel>,
): ReportRow {
  const breakdown = record(row['RegionsByStatus']);
  const statuses = Object.fromEntries(statusColumns.map(({ field }) => [
    field,
    {
      count: row[field],
      regions: labels ? entries(breakdown[field]).flatMap(value => {
        const item = record(value);
        const id = item['RegionId'];
        const count = item['Count'];
        return typeof id === 'string' && typeof count === 'number' && count > 0
          ? [{ id, count, region: labels.get(id) }]
          : [];
      }) : [],
    },
  ])) as Record<Status, StatusCell>;
  return { month: row.Month, total: row.Total, statuses };
}

function isCountRow(value: unknown): value is Record<string, unknown> & { Month: string; Total: number } & Record<Status, number> {
  const row = record(value);
  return typeof row['Month'] === 'string' && typeof row['Total'] === 'number'
    && statusColumns.every(({ field }) => typeof row[field] === 'number');
}

function entries(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}
